import React, { FC, useCallback } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

import Button from '../../../../components/common/Button';
import Input from '../../../../components/common/Input';
import Accordion from '../../../../components/elements/Accordion';
import Confirmation from '../../../../components/elements/Confirmation';
import { theme } from '../../../../global';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { useModal } from '../../../../hooks/useModal';
import { useAppSelector } from '../../../../hooks/useSelector';
import { ITeamDuelState } from '../../../../lib/interfaces/ITeamDuel';
import { getTeamDuel } from '../../../../store/selectors/settings.selector';
import { TeamDuelInitialState, settingsActions } from '../../../../store/slices/settings.slice';
import { teamActions } from '../../../../store/slices/team.slice';

import {
  RESET_SETTINGS_ABORT_TEXT,
  RESET_SETTINGS_CONFIRM_TEXT,
  RESET_SETTINGS_CONFIRMATION,
} from './config';
import * as S from './styles';

export interface ITeamDuelFormProps {
  toggleFlyout: () => void;
}

const TeamDuelForm: FC<ITeamDuelFormProps> = ({ toggleFlyout }) => {
  const dispatch = useAppDispatch();
  const initialState = useAppSelector(getTeamDuel);
  const [params, setParams] = useSearchParams();
  const { isActive, toggleHandler: toggleConfirmation } = useModal({ handler: toggleFlyout });
  const { register, handleSubmit, reset } = useForm<ITeamDuelState>({
    defaultValues: initialState,
  });

  const onSubmit: SubmitHandler<ITeamDuelState> = useCallback(
    (data) => {
      dispatch(settingsActions.setTeamDuel(data));
      const remainingTime = data.teamDuel.roundTime.min * 60 + data.teamDuel.roundTime.sec;
      dispatch(teamActions.setPlayerRemainingTime({ player: 'player1', remainingTime }));
      dispatch(teamActions.setPlayerRemainingTime({ player: 'player2', remainingTime }));
      dispatch(
        settingsActions.setPartialDuel({
          player: 'player1',
          settings: { min: data.teamDuel.roundTime.min, sec: data.teamDuel.roundTime.sec },
        }),
      );
      dispatch(
        settingsActions.setPartialDuel({
          player: 'player2',
          settings: { min: data.teamDuel.roundTime.min, sec: data.teamDuel.roundTime.sec },
        }),
      );
      dispatch(teamActions.updateTimerKeys());
      params.set('game_mode', 'team game');
      setParams(params);
      toggleFlyout();
    },
    [dispatch, params, setParams, toggleFlyout],
  );

  const onConfirmReset = useCallback(() => {
    dispatch(settingsActions.resetTeamDuel());
    toggleConfirmation();
    dispatch(teamActions.updateTimerKeys());
    reset(TeamDuelInitialState);
  }, [dispatch, reset, toggleConfirmation]);

  return (
    <>
      <S.Form onSubmit={handleSubmit(onSubmit)}>
        <Accordion title="game timer">
          <S.AccordionElementsContainer>
            <Input
              variant="time"
              label="round time"
              minProps={{
                ...register('teamDuel.roundTime.min', { min: 0, valueAsNumber: true }),
                min: 0,
              }}
              secProps={{
                ...register('teamDuel.roundTime.sec', { min: 0, valueAsNumber: true }),
                min: 0,
              }}
            />
            <Input
              variant="toggle"
              label="End of Round Sound"
              {...register('teamDuel.isEnabledEndOfRoundSound')}
              defaultChecked={initialState.teamDuel.isEnabledEndOfRoundSound}
            />
          </S.AccordionElementsContainer>
        </Accordion>

        <S.Line />

        <Accordion
          title="pause"
          $bgColor={theme.colors.active_accordion}
        >
          <S.AccordionElementsContainer>
            <Input
              variant="time"
              label="pause time"
              minProps={{
                ...register('pause.pauseTime.min', { min: 0, valueAsNumber: true }),
                min: 0,
              }}
              secProps={{
                ...register('pause.pauseTime.sec', { min: 0, valueAsNumber: true }),
                min: 0,
              }}
            />

            <Input
              variant="toggle"
              label="Start blinking"
              {...register('pause.isStartBlinking')}
              defaultChecked={initialState.pause.isStartBlinking}
            />

            <Input
              variant="toggle"
              label="End of Pause Sound"
              {...register('pause.isEnabledEndOfPauseSound')}
              defaultChecked={initialState.pause.isEnabledEndOfPauseSound}
            />
          </S.AccordionElementsContainer>
        </Accordion>

        <S.Line />
        <Accordion title="Voters' timer">
          <S.AccordionElementsContainer>
            <Input
              variant="time"
              label="Voter's time"
              minProps={{
                ...register('votersTimer.pauseTime.min', { min: 0, valueAsNumber: true }),
                min: 0,
              }}
              secProps={{
                ...register('votersTimer.pauseTime.sec', { min: 0, valueAsNumber: true }),
                min: 0,
              }}
            />

            <Input
              variant="toggle"
              label="Start blinking"
              {...register('votersTimer.isStartBlinking')}
              defaultChecked={initialState.votersTimer.isStartBlinking}
            />

            <Input
              variant="toggle"
              label="End of Pause Sound"
              {...register('votersTimer.isEnabledEndOfPauseSound')}
              defaultChecked={initialState.votersTimer.isEnabledEndOfPauseSound}
            />
          </S.AccordionElementsContainer>
        </Accordion>

        <S.ButtonsContainer>
          <Button
            variant="outline"
            $size="large"
            type="button"
            onClick={toggleConfirmation}
          >
            reset settings
          </Button>
          <Button
            variant="primary"
            $size="large"
            type="submit"
          >
            apply
          </Button>
        </S.ButtonsContainer>
      </S.Form>
      <Confirmation
        description={RESET_SETTINGS_CONFIRMATION}
        confirmText={RESET_SETTINGS_CONFIRM_TEXT}
        abortText={RESET_SETTINGS_ABORT_TEXT}
        title="reset settings"
        isActive={isActive}
        onConfirm={onConfirmReset}
        onClose={toggleConfirmation}
        onAbort={toggleConfirmation}
      />
    </>
  );
};

export default TeamDuelForm;
